import fuzzysearch from 'fuzzysearch';
import { Eta } from 'eta/core';

import template from './search.ejs?raw';

const RECENT_KEY = 'chh:recent-searches';
const RECENT_MAX = 5;

const SUGGEST_ENDPOINT = '/api/search/suggestions';
const SUGGEST_DEBOUNCE = 300;

const GROUPS = [
  { id: 'suggestions', filter: false },
  { id: 'recent' },
  { id: 'topics' },
  { id: 'entryTypes' },
];

const eta = new Eta({ useWith: true });

class SearchElement extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('[data-ref="input"]');
    this._panel = this.querySelector('[data-ref="panel"]');
    this._listbox = this.querySelector('[data-ref="listbox"]');
    this._form = this.querySelector('.search__form');

    if (!this._input || !this._panel || !this._listbox) return;

    this._action = this.dataset.action || '/search';
    this._inline = this.hasAttribute('data-inline');
    this._listboxId = this._listbox.id || 'search-suggestions';
    this._options = [];
    this._activeIndex = -1;
    this._suggestions = [];
    this._open = false;

    this._data = this._readData();

    this._controller = new AbortController();
    const { signal } = this._controller;

    this._input.addEventListener(
      'input',
      () => {
        this.open();
        this._render();
        this._fetchSuggestions();
      },
      { signal }
    );
    this._input.addEventListener('keydown', (e) => this._onKeydown(e), { signal });

    this._form.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        this._navigate(this._input.value, { record: true });
      },
      { signal }
    );

    // Keep input focus when clicking anywhere in the panel (including its
    // padding) so it doesn't blur and close before an option's click lands.
    this._panel.addEventListener('mousedown', (e) => e.preventDefault(), { signal });
    this._listbox.addEventListener(
      'click',
      (e) => {
        const option = e.target.closest('[role="option"]');
        if (!option) return;
        this._activate(option);
      },
      { signal }
    );

    if (this._inline) {
      this.open();
    } else {
      this._input.addEventListener('focus', () => this.open(), { signal });
      this.addEventListener(
        'focusout',
        (e) => {
          if (!this.contains(e.relatedTarget)) this.close();
        },
        { signal }
      );
      document.addEventListener(
        'click',
        (e) => {
          if (!this.contains(e.target)) this.close();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._suggestController?.abort();
    clearTimeout(this._suggestTimer);
  }

  open() {
    if (this._open) return;
    this._open = true;
    if (!this._inline) {
      this.dispatchEvent(new CustomEvent('chh-search:open', { bubbles: true }));
    }
    this._render();
  }

  close() {
    if (this._inline || !this._open) return;
    this._open = false;
    this._panel.hidden = true;
    this._input.setAttribute('aria-expanded', 'false');
    this._clearActive();
    this.dispatchEvent(new CustomEvent('chh-search:close', { bubbles: true }));
  }

  _readData() {
    const script = this.querySelector('[data-ref="data"]');
    try {
      const parsed = JSON.parse(script?.textContent || '{}');
      return {
        labels: parsed.labels && typeof parsed.labels === 'object' ? parsed.labels : {},
        topics: Array.isArray(parsed.topics) ? parsed.topics : [],
        entryTypes: Array.isArray(parsed.entryTypes) ? parsed.entryTypes : [],
      };
    } catch {
      return { labels: {}, topics: [], entryTypes: [] };
    }
  }

  _readRecents() {
    try {
      const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
    } catch {
      return [];
    }
  }

  _recordRecent(term) {
    const value = term.trim();
    if (!value) return;
    try {
      const existing = this._readRecents().filter(
        (item) => item.toLowerCase() !== value.toLowerCase()
      );
      existing.unshift(value);
      localStorage.setItem(RECENT_KEY, JSON.stringify(existing.slice(0, RECENT_MAX)));
    } catch {
      // Storage unavailable (private mode / quota) — recents are non-essential.
    }
  }

  _itemsFor(groupId) {
    if (groupId === 'recent') {
      return this._readRecents().map((term) => ({
        label: term,
        term,
        icon: 'search-new',
        record: true,
      }));
    }
    if (groupId === 'topics') {
      return this._data.topics;
    }
    if (groupId === 'suggestions') {
      return this._suggestions;
    }
    return this._data.entryTypes;
  }

  _render() {
    const term = this._input.value.trim().toLowerCase();
    let optionIndex = 0;

    const groups = GROUPS.map((group) => {
      const noFilter = group.filter === false;
      const items = this._itemsFor(group.id).filter(
        (item) => noFilter || !term || fuzzysearch(term, item.label.toLowerCase())
      );
      if (!items.length) return null;

      return {
        id: group.id,
        heading: this._data.labels[group.id] ?? group.id,
        headingId: `${this._listboxId}-${group.id}-label`,
        options: items.map((item) => ({
          id: `${this._listboxId}-opt-${optionIndex++}`,
          label: item.label,
          record: Boolean(item.record),
          icon: item.icon,
          url: item.url,
          term: item.url ? null : item.term,
        })),
      };
    }).filter(Boolean);

    this._listbox.innerHTML = eta.renderString(template, { groups });

    this._options = Array.from(this._listbox.querySelectorAll('[role="option"]'));
    this._clearActive();

    // Only surface the panel when it has something to show.
    const hidden = !this._inline && (!this._open || !groups.length);
    this._panel.hidden = hidden;
    this._input.setAttribute('aria-expanded', String(!hidden));
  }

  _onKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.open();
        this._moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.open();
        this._moveActive(-1);
        break;
      case 'Home':
        if (this._options.length) {
          e.preventDefault();
          this._setActive(0);
        }
        break;
      case 'End':
        if (this._options.length) {
          e.preventDefault();
          this._setActive(this._options.length - 1);
        }
        break;
      case 'Enter':
        if (this._activeIndex >= 0) {
          e.preventDefault();
          this._activate(this._options[this._activeIndex]);
        }
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  _moveActive(delta) {
    if (!this._options.length) return;
    const next =
      this._activeIndex < 0 && delta < 0
        ? this._options.length - 1
        : (this._activeIndex + delta + this._options.length) % this._options.length;
    this._setActive(next);
  }

  _setActive(index) {
    this._clearActive();
    const option = this._options[index];
    if (!option) return;
    this._activeIndex = index;
    option.classList.add('search__option--active');
    this._input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  _clearActive() {
    if (this._activeIndex >= 0) {
      this._options[this._activeIndex]?.classList.remove('search__option--active');
    }
    this._activeIndex = -1;
    this._input.removeAttribute('aria-activedescendant');
  }

  _activate(option) {
    if (!option) return;
    if (option.dataset.url) {
      window.location.assign(option.dataset.url);
      return;
    }
    this._navigate(option.dataset.term, { record: option.dataset.record === 'true' });
  }

  _fetchSuggestions() {
    const term = this._input.value.trim();
    clearTimeout(this._suggestTimer);
    this._suggestController?.abort();

    // Clear stale suggestions right away, then fetch fresh ones after a pause.
    this._setSuggestions([]);
    if (!term) return;

    this._suggestTimer = setTimeout(() => this._loadSuggestions(term), SUGGEST_DEBOUNCE);
  }

  async _loadSuggestions(term) {
    this._suggestController?.abort();
    this._suggestController = new AbortController();

    try {
      const url = `${SUGGEST_ENDPOINT}?q=${encodeURIComponent(term)}`;
      const response = await fetch(url, { signal: this._suggestController.signal });
      if (!response.ok) return;

      const data = await response.json();
      const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      this._setSuggestions(
        suggestions.map((suggestion) => ({ label: suggestion.title, url: suggestion.url }))
      );
    } catch {
      // Aborted or network error — suggestions are non-essential.
    }
  }

  _setSuggestions(suggestions) {
    this._suggestions = suggestions;
    if (!this._open) return;
    this._render();
  }

  _navigate(term, { record } = { record: false }) {
    const value = (term || '').trim();
    if (!value) return;
    if (record) this._recordRecent(value);
    const separator = this._action.includes('?') ? '&' : '?';
    window.location.assign(`${this._action}${separator}q=${encodeURIComponent(value)}`);
  }
}

customElements.define('chh-search', SearchElement);

export default SearchElement;
